// Sheet-cut optimizer (greedy, axis-aligned, no rotation mixing).
// For a stock sheet (e.g. 48 × 96 in plywood) and a target part (e.g. 12 × 24
// in), figure out how many parts fit per sheet and how many sheets to buy to
// reach the requested quantity. We try both orientations (part as-is and
// rotated 90°) and pick whichever yields more parts.
//
// This is intentionally a SIMPLE bin-pack: we do not solve the full 2D cutting
// stock problem (NP-hard). For a single uniform part size — which is the
// dominant DIY case — a grid layout is optimal or near-optimal once you
// account for kerf, and it produces a layout the user can actually mark out
// with a tape measure.
import { round } from '../lib/format'
import type { AccessoryLine } from '../data/companions'
import type { Locale } from '../i18n/locale'

export interface SheetCutInputs {
  // forward: "I need N parts" → how many sheets to buy.
  // reverse: "I have N sheets" → how many parts I can cut.
  mode: 'forward' | 'reverse'
  sheetWidthIn: number
  sheetHeightIn: number
  partWidthIn: number
  partHeightIn: number
  qtyNeeded: number
  kerfIn: number              // saw kerf width — subtract from both axes when packing
  sheetsHave: number          // reverse mode: stock sheets on hand
}

export interface SheetCutResult {
  partsPerSheet: number
  partsPerRow: number
  partsPerCol: number
  bestOrientation: 'as-is' | 'rotated'
  sheetsNeeded: number
  totalPartsCut: number
  leftoverParts: number
  maxPartsFromSheets: number  // reverse mode: parts you can cut from `sheetsHave`
  wasteAreaPerSheet: number   // sq in
  wastePctPerSheet: number
  partAreaIn2: number
  sheetAreaIn2: number
  lines: AccessoryLine[]
}

export const DEFAULT_SHEET_CUT: SheetCutInputs = {
  mode: 'forward',
  sheetWidthIn: 48,
  sheetHeightIn: 96,
  partWidthIn: 12,
  partHeightIn: 24,
  qtyNeeded: 20,
  kerfIn: 0.125,              // standard ~1/8 in for a circular saw blade
  sheetsHave: 4,
}

function packGrid(sheetW: number, sheetH: number, partW: number, partH: number, kerf: number) {
  if (partW <= 0 || partH <= 0) return { perRow: 0, perCol: 0, total: 0 }
  // First cut consumes no kerf on the outside edge, every subsequent cut adds 1 kerf.
  // Effective layout: n parts fit along axis L if n*part + (n-1)*kerf ≤ L
  // → n ≤ (L + kerf) / (part + kerf)
  const perRow = Math.max(0, Math.floor((sheetW + kerf) / (partW + kerf)))
  const perCol = Math.max(0, Math.floor((sheetH + kerf) / (partH + kerf)))
  return { perRow, perCol, total: perRow * perCol }
}

export function computeSheetCut(i: SheetCutInputs, locale: Locale = 'en'): SheetCutResult {
  const tr = (en: string, fr: string) => (locale === 'fr' ? fr : en)
  const sheetW = Math.max(0, i.sheetWidthIn)
  const sheetH = Math.max(0, i.sheetHeightIn)
  const partW = Math.max(0, i.partWidthIn)
  const partH = Math.max(0, i.partHeightIn)
  const qty = Math.max(0, Math.floor(i.qtyNeeded))
  const kerf = Math.max(0, i.kerfIn)
  const sheetsHave = Math.max(0, Math.floor(i.sheetsHave))

  const asIs = packGrid(sheetW, sheetH, partW, partH, kerf)
  const rotated = packGrid(sheetW, sheetH, partH, partW, kerf)

  const bestOrientation: 'as-is' | 'rotated' = rotated.total > asIs.total ? 'rotated' : 'as-is'
  const best = bestOrientation === 'rotated' ? rotated : asIs

  const sheetsNeeded = best.total > 0 && qty > 0 ? Math.ceil(qty / best.total) : 0
  const totalPartsCut = sheetsNeeded * best.total
  const leftoverParts = Math.max(0, totalPartsCut - qty)
  // Reverse mode: how many parts those on-hand sheets yield.
  const maxPartsFromSheets = best.total * sheetsHave
  const partArea = partW * partH
  const sheetArea = sheetW * sheetH
  const usedAreaPerSheet = best.total * partArea
  const wasteAreaPerSheet = Math.max(0, sheetArea - usedAreaPerSheet)
  const wastePct = sheetArea > 0 ? (wasteAreaPerSheet / sheetArea) * 100 : 0

  const lines: AccessoryLine[] = []
  // Forward mode lists the sheets to buy; reverse mode assumes you already have
  // them and just lists the blade/tools to cut them. Both gate on a real layout.
  const hasLayout = best.total > 0 && (i.mode === 'forward' ? sheetsNeeded > 0 : sheetsHave > 0)
  if (hasLayout) {
    if (i.mode === 'forward') {
      lines.push({
        item: 'plywood sheet 4x8 birch baltic',
        label: tr('Sheet stock', 'Panneaux'),
        kind: 'consumable',
        qty: sheetsNeeded,
        unit: 'sheet',
        note: tr(`${sheetsNeeded} sheets at ${sheetW} × ${sheetH} in — round up so you have one full backup in case of a bad cut.`, `${sheetsNeeded} panneaux de ${sheetW} × ${sheetH} po — arrondissez à la hausse pour avoir un panneau de réserve en cas de mauvaise coupe.`),
      })
    }
    lines.push({
      item: 'circular saw blade fine plywood 60 tooth',
      label: tr('Fine-tooth saw blade', 'Lame de scie à dents fines'),
      kind: 'consumable',
      qty: 1,
      unit: 'piece',
      note: tr('A 60+ tooth blade gives clean, low-tearout cuts in plywood and melamine.', 'Une lame de 60 dents ou plus produit des coupes propres et sans éclats dans le contreplaqué et la mélamine.'),
    })
    lines.push({
      item: 'track saw guide rail festool makita',
      label: tr('Track saw / straight-edge guide', 'Scie à rail / guide droit'),
      kind: 'tool',
      optional: true,
      note: tr('A guide rail beats a freehand cut every time — best for breaking down full sheets accurately.', 'Un guide à rail bat la coupe à main levée à tous coups — idéal pour débiter les panneaux avec précision.'),
    })
    lines.push({
      item: 'sawhorse pair plastic folding',
      label: tr('Sawhorses', 'Chevalets de sciage'),
      kind: 'tool',
      optional: true,
      note: tr('A pair of sawhorses with sacrificial foam or 2×4s underneath supports the sheet through the cut.', 'Une paire de chevalets avec mousse sacrificielle ou 2×4 dessous supporte le panneau pendant la coupe.'),
    })
    lines.push({
      item: 'measuring tape 25 ft contractor',
      label: tr('Measuring tape', 'Ruban à mesurer'),
      kind: 'tool',
      note: tr('Mark every cut twice and only cut once — saves an entire sheet.', 'Marquez chaque coupe deux fois et coupez une seule fois — cela peut sauver un panneau entier.'),
    })
  }

  return {
    partsPerSheet: best.total,
    partsPerRow: best.perRow,
    partsPerCol: best.perCol,
    bestOrientation,
    sheetsNeeded,
    totalPartsCut,
    leftoverParts,
    maxPartsFromSheets,
    wasteAreaPerSheet: round(wasteAreaPerSheet, 1),
    wastePctPerSheet: round(wastePct, 1),
    partAreaIn2: round(partArea, 2),
    sheetAreaIn2: round(sheetArea, 2),
    lines,
  }
}
